<template>
  <div>
    <div class="row bg-grey pt-3">
      <div class="col-12">
        <h3 class="title-h3 border-bottom pb-3">
          <button type="button"
                  class="btn btn-back"
                  @click="$router.push('/customers')"
          />
          Back
        </h3>
      </div>
    </div>
    <div class="l-bg-white">
      <div class="row my-3">
        <div class="col-12 text-right" v-if="$_can('leads.store')">
          <button type="button"
                  class="btn btn-primary"
                  @click="toggleCreateSidebar"
          >
            Create lead
          </button>
        </div>
      </div>
      <form @submit.prevent="submit"
            autocomplete="off"
            @keydown="errors.clear()"
      >
        <div class="row mb-3">
          <div class="col-4">
            <div class="select-wrap">
              <label>Company</label>
              <simple-select v-model="customer.company_id"
                             :options="companies"
                             placeholder="Select Company"
              />
            </div>
            <error-field :errors="errors" field="company_id" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-4">
            <div class="select-wrap">
              <label>First name</label>
              <input v-model="customer.first_name"
                     type="text"
                     autocomplete="no"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="first_name" />
          </div>
          <div class="col-4">
            <div class="select-wrap">
              <label>Last name</label>
              <input v-model="customer.last_name"
                     type="text"
                     autocomplete="no"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="last_name" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-4">
            <div class="select-wrap">
              <label>Email</label>
              <input v-model="customer.email"
                     type="email"
                     autocomplete="no"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="email" />
          </div>
          <div class="col-4">
            <div class="select-wrap">
              <label>Phone</label>
              <input v-model="customer.phone"
                     type="text"
                     class="form-control"
                     @keydown="maskPhone"
                     placeholder="Enter Phone"
                     autocomplete="no"
                     maxlength="14"
              />
            </div>
            <error-field :errors="errors" field="phone" />
          </div>
        </div>
        <!-- Additional contacts -->
        <div v-for="(contact, index) in customer.contacts"
             :key="contact.id"
        >
          <div class="row" v-if="index === 0">
            <div class="col-md-12 mb-0">
              <label>ADDITIONAL CONTACTS</label>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-4">
              <div class="select-wrap">
                <label>Email</label>
                <input v-model="contact.email"
                       type="email"
                       autocomplete="no"
                       placeholder="Enter Email"
                       class="form-control"
                />
              </div>
              <error-field :errors="errors" :field="`contacts.${index}.email`" />
            </div>
            <div class="col-4">
              <div class="select-wrap">
                <label>Phone</label>
                <input v-model="contact.phone"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       @keydown="maskAdditionalPhone(index, $event)"
                       placeholder="Enter Phone"
                       maxlength="14"
                />
              </div>
              <error-field :errors="errors" :field="`contacts.${index}.phone`" />
            </div>
            <div class="col-4">
              <button type="button"
                      class="btn btn-clear p-1 mt-3"
                      @click="removeContact(index)"
              >
                <span class="mod-icon-clear" />
              </button>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col">
            <button type="button"
                    class="btn add-button"
                    @click="addAdditionalContact"
            >
              <span>
                <i class="fa fa-plus" aria-hidden="true" /></span>
              Add additional contact
            </button>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col">
            <button type="button"
                    class="btn btn-danger"
                    @click="remove(customer.id)"
                    v-if="$_can('companies.customers.destroy')"
            >
              Delete
            </button>
          </div>
          <div class="col">
            <button class="btn btn-primary float-right ld-ext-right"
                    :class="loadingButton ? 'running' : ''"
                    :disabled="buttonDisabled"
                    v-if="$_can('companies.customers.update')"
            >
              Save
              <div class="ld ld-ball ld-flip" />
            </button>
          </div>
        </div>
        <error-field :errors="errors" field="deletion" />
      </form>
    </div>

    <!-- Create Lead -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showCreateSidebar" @click.self="hideCreateSidebar">
        <leads-create v-if="showCreateSidebar" class="sidebar-right" @cancel="hideCreateSidebar"
                      :customer="customer"
                      @leads-search="checkDuplicateLeads"
                      :searching-duplicate-leads="searchingDuplicateLeads"
                      @leads-clear="clearDuplicateLeads"
        />
      </div>
    </transition>

    <!-- Duplicate lead -->
    <transition name="slide-fade">
      <div class="duplicate-lead-wrap" v-if="duplicateLeads.length">
        <h3>Existing leads for the same customer have been found.</h3>
        <div class="duplicate-lead-box-overflow">
          <div class="duplicate-lead-box"
               :class="{'lead-can-access': lead.can_access === true}"
               v-for="lead in duplicateLeads" :key="lead.id"
          >
            <router-link :to="`/leads/edit/${lead.id}?q=${lead.ref_id}`"
                         :class="[lead.can_access === true ? 'pointer' : 'pointer-events-none']"
            >
              <div class="col-md-4 px-0 mb-0">
                <span class="refId">{{ lead.ref_id }}</span>
              </div>
              <div class="col-md-6 px-0 mb-0">
                <span class="name">{{ lead.customer }}</span>
              </div>
              <div class="col-md-2 px-0 mb-0 text-right">
                <span class="mod-icon-arrow" />
              </div>
              <div class="col-md-12 px-0 mb-0 pt-1">
                <div class="input-wrap">
                  <label>Assigned to</label>
                  <p>{{ lead.cluster_user ? lead.cluster_user : '-' }}</p>
                </div>
              </div>
              <div class="col-md-8 px-0 mb-0 pt-2">
                <div class="input-wrap">
                  <label>Company</label>
                  <p>{{ lead.company }}</p>
                </div>
              </div>
              <div class="col-md-4 px-0 mb-0 pt-2 text-right">
                <template v-if="lead.status">
                  <span v-if="lead.status_id === 1 || lead.status_id === 7"
                        :class="'btn-status btn-active'"
                  >{{ lead.status.name }}</span>
                  <span v-if="lead.status_id === 2"
                        :class="'btn-status btn-booked'"
                  >{{ lead.status.name }}</span>
                  <span v-if="lead.status_id === 3 || lead.status_id === 6"
                        :class="'btn-status btn-closed'"
                  >{{ lead.status.name }}</span>
                  <span v-if="lead.status_id === 4 || lead.status_id === 8"
                        :class="'btn-status btn-ncover'"
                  >{{ lead.status.name }}</span>
                  <span v-if="lead.status_id === 5"
                        :class="'btn-status btn-rtbook'"
                  >{{ lead.status.name }}</span>
                </template>
              </div>
            </router-link>
          </div>
          <error-field :errors="errors" field="leads" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import toastr from 'toastr';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import LeadsCreate from '../leads/LeadsCreate';
import PhoneMask from '../../classes/PhoneMask';

export default {
  components: {
    LeadsCreate,
    ErrorField,
  },
  data: () => ({
    customer: {
      first_name: '',
      last_name: '',
      email: null,
      phone: null,
      company_id: null,
      contacts: [],
    },
    loadingButton: false,
    buttonDisabled: false,
    showCreateSidebar: false,
    duplicateLeads: [],
    searchingDuplicateLeads: 0,
    closeDuplicate: false,
    phoneMask: new PhoneMask(),
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      companies: state => state.companies.companies.data,
    }),
  },
  mounted() {
    this.getCustomer(this.$route.params.id);
  },
  methods: {
    getCustomer(id) {
      axios
        .get(`customers/${id}`)
        .then(res => {
          this.customer = res.data.data;
          this.$set(this.customer, 'phone', this.phoneMask.maskedPhone(res.data.data.phone));
          this.customer.contacts.forEach(el => {
            el.phone = this.phoneMask.maskedPhone(el.phone);
          });
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    submit() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .put(`customers/${this.$route.params.id}`, { ...this.customer })
        .then(() => {
          toastr.success('Successfully edited customer');
          this.$router.push('/customers');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    remove(id) {
      axios
        .delete(`customers/${id}`)
        .then(() => {
          this.$router.push('/customers');
          toastr.success('Successfully deleted customer');
        })
        .catch(err => {
          toastr.error(err.response.data.error.deletion[0], '', { timeOut: 0 });
        });
    },
    clearError(event) {
      this.errors.clear(event.target.name);
    },
    toggleCreateSidebar() {
      this.$router.push(`?customer_id=${this.$route.params.id}`);
      this.showCreateSidebar = true;
    },
    addAdditionalContact() {
      this.customer.contacts.push({});
    },
    removeContact(index) {
      this.customer.contacts.splice(index, 1);
    },
    maskPhone() {
      this.$nextTick(() => {
        this.$set(this.customer, 'phone', this.phoneMask.maskedPhone(this.customer.phone));
      });
    },
    maskAdditionalPhone(index, event) {
      this.$nextTick(() => {
        this.$set(
          this.customer.contacts[index],
          'phone',
          this.phoneMask.maskedPhone(this.customer.contacts[index].phone),
        );
        this.clearError(event);
      });
    },
    hideCreateSidebar() {
      this.duplicateLeads = [];
      this.showCreateSidebar = false;
    },
    checkDuplicateLeads(lead) {
      let url = `leads/duplicates?company_id=${lead.company_id}`;
      if (lead.phone) {
        url += `&phone=${encodeURIComponent(lead.phone)}`;
      }
      if (lead.email) {
        url += `&email=${encodeURIComponent(lead.email)}`;
      }
      axios
        .get(`${url}`)
        .then(result => {
          if (this.showCreateSidebar) {
            this.duplicateLeads = result.data.data;
          }
        })
        .catch(err => {
          this.duplicateLeads = [];
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.searchingDuplicateLeads = this.duplicateLeads.length;
        });
    },
    clearDuplicateLeads() {
      this.duplicateLeads = [];
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/customers/CustomersEdit.vue